<template>
  <ReportWrapper
    :loading="loading"
    :on-load="loadReport"
    :tab-lv1-index="tabLv1Index"
    :tab-lv2-index="tabLv2Index"
    :showProviderFilter="isAdmin"
  >
    <ReportTable minWidth="860px">
      <thead>
        <tr>
          <th><span>Name</span></th>
          <th><span>All appts</span></th>
          <th><span>Completed appts</span></th>
          <th>
            <TableHeaderWithTooltip
              title="Primary appts"
              :tooltip="tooltips['Primary appts']"
            />
          </th>
          <th>
            <TableHeaderWithTooltip
              title="First-seen patients"
              :tooltip="tooltips['First-seen patients']"
            />
          </th>
          <th>
            <TableHeaderWithTooltip
              title="First-treated patients"
              :tooltip="tooltips['First-treated patients']"
            />
          </th>
          <th>
            <TableHeaderWithTooltip
              title="Unique patients met"
              :tooltip="tooltips['Unique patients met']"
            />
          </th>
        </tr>
      </thead>
      <tbody v-if="!loading">
        <!-- clientAdmin view -->
        <template v-if="isAdmin && patientsPerProvider.length">
          <tr
            v-for="(item, index) in patientsPerProvider"
            :key="index"
            :class="{
              active: item.isCurrentUserRecord,
              median: item.isMedian,
              hidden: !item.isActive || item.isHidden || item.isHiddenReport,
            }"
          >
            <td>{{ item.fullName }}</td>
            <td>{{ item.allAppointments }}</td>
            <td>{{ item.completedAppointments }}</td>
            <td>{{ item.primaryCompletedAppointments }}</td>
            <td>{{ item.firstSeenPatients }}</td>
            <td>{{ item.firstTreatedPatients }}</td>
            <td>{{ item.uniquePatientsByCompletedAppointments }}</td>
          </tr>

          <tr
            v-if="!userHasRecordInReport"
            class="median"
          >
            <td>Median</td>
            <td>{{ median.allAppointments }}</td>
            <td>{{ median.completedAppointments }}</td>
            <td>{{ median.primaryCompletedAppointments }}</td>
            <td>{{ median.firstSeenPatients }}</td>
            <td>{{ median.firstTreatedPatients }}</td>
            <td>{{ median.uniquePatientsByCompletedAppointments }}</td>
          </tr>

          <tr>
            <td>Whole clinic</td>
            <td>{{ clinicMetricsByName.allAppointments }}</td>
            <td>{{ clinicMetricsByName.completedAppointments }}</td>
            <td></td>
            <td>{{ clinicMetricsByName.firstSeenPatients }}</td>
            <td>{{ clinicMetricsByName.firstTreatedPatients }}</td>
            <td>{{ clinicMetricsByName.uniquePatientsByCompletedAppointments }}</td>
          </tr>
        </template>

        <!-- clientUser view -->
        <template v-else-if="!isAdmin && userRecord">
          <tr class="active">
            <td>{{ userRecord.fullName }}</td>
            <td>{{ userRecord.allAppointments }}</td>
            <td>{{ userRecord.completedAppointments }}</td>
            <td>{{ userRecord.primaryCompletedAppointments }}</td>
            <td>{{ userRecord.firstSeenPatients }}</td>
            <td>{{ userRecord.firstTreatedPatients }}</td>
            <td>{{ userRecord.uniquePatientsByCompletedAppointments }}</td>
          </tr>

          <tr v-if="median">
            <td>Median</td>
            <td>{{ median.allAppointments }}</td>
            <td>{{ median.completedAppointments }}</td>
            <td>{{ median.primaryCompletedAppointments }}</td>
            <td>{{ median.firstSeenPatients }}</td>
            <td>{{ median.firstTreatedPatients }}</td>
            <td>{{ median.uniquePatientsByCompletedAppointments }}</td>
          </tr>

          <tr v-if="averageTop10Percent">
            <td>Average top 10 percent</td>
            <td>{{ averageTop10Percent.allAppointments }}</td>
            <td>{{ averageTop10Percent.completedAppointments }}</td>
            <td>{{ averageTop10Percent.primaryCompletedAppointments }}</td>
            <td>{{ averageTop10Percent.firstSeenPatients }}</td>
            <td>{{ averageTop10Percent.firstTreatedPatients }}</td>
            <td>{{ averageTop10Percent.uniquePatientsByCompletedAppointments }}</td>
          </tr>

          <tr v-if="clinicMetricsByName">
            <td>Whole clinic</td>
            <td>{{ clinicMetricsByName.allAppointments }}</td>
            <td>{{ clinicMetricsByName.completedAppointments }}</td>
            <td></td>
            <td>{{ clinicMetricsByName.firstSeenPatients }}</td>
            <td>{{ clinicMetricsByName.firstTreatedPatients }}</td>
            <td>{{ clinicMetricsByName.uniquePatientsByCompletedAppointments }}</td>
          </tr>
        </template>

        <NoRecordsFound
          v-else
          colspan="7"
        />
      </tbody>
    </ReportTable>

    <MetricDefinitionList
      v-if="!loading"
      :metrics="tooltips"
    />
  </ReportWrapper>
</template>

<script>
import DataService from '../../../../_services/DataService';
import MetricDefinitionList from '../../../components/Reporting/MetricDefinitionList';
import NoRecordsFound from '../../../components/Reporting/NoRecordsFound';
import ReportTable from '../../../components/Reporting/ReportTable.vue';
import ReportWrapper from '../../../components/Reporting/ReportWrapper';
import TableHeaderWithTooltip from '../../../components/Reporting/TableHeaderWithTooltip';
import Utils from '../../../utils';

export default {
  name: 'DDSPatients',
  components: {
    MetricDefinitionList,
    NoRecordsFound,
    ReportTable,
    ReportWrapper,
    TableHeaderWithTooltip,
  },
  inject: ['shouldShowHiddenProviders'],
  props: ['tabLv1Index', 'tabLv2Index'],
  data() {
    return {
      patientsPerProviderAll: [],
      clinicMetricsByName: null,
      userRole: '',
      userRecord: null,
      averageTop10Percent: null,
      median: null,
      loading: false,
      tooltips: {
        'Primary appts': 'Completed appointments whose main/primary provider is the current DDS',
        'First-seen patients': 'Patients having their first appointments completed in the selected time period',
        'First-treated patients': 'Patients having their first procedures done in the selected time period',
        'Unique patients met': 'Amount of unique patients met in the selected time period',
      },
    };
  },
  computed: {
    isAdmin() {
      return this.userRole === 'clientAdmin';
    },
    userHasRecordInReport() {
      return !!this.userRecord;
    },
    showHiddenProviders() {
      return this.shouldShowHiddenProviders();
    },
    patientsPerProvider() {
      return this.showHiddenProviders
        ? this.patientsPerProviderAll
        : this.patientsPerProviderAll.filter(this.providerIsVisible);
    },
  },
  methods: {
    async loadReport(dateRange) {
      const { start, end } = dateRange;
      this.loading = true;

      const result = await DataService.getDDSPatientsReport({ start, end });

      if (result.status === 200) {
        const patientsPerProvider = result.data.patientsPerProvider ?? [];
        this.patientsPerProviderAll = patientsPerProvider;

        Utils.prependCurrentUserRecordAndMedian(patientsPerProvider, result.data.median);

        this.clinicMetricsByName = result.data.clinicMetricsByName;
        this.userRecord = result.data.userRecord;
        this.averageTop10Percent = result.data.averageTop10Percent;
        this.median = result.data.median;
      } else {
        this.patientsPerProviderAll = [];
      }

      this.loading = false;
    },
    providerIsVisible(provider) {
      return provider.isActive && !provider.isHidden && !provider.isHiddenReport;
    },
  },
  mounted() {
    this.userRole = DataService.getRole();
  },
};
</script>

<style scoped>
th,
td {
  padding: 8px 16px !important;
}
</style>
